import { useState } from 'react'
import { useAsync } from '../hooks/useAsync'
import { fetchSeasons, fetchStandings } from '../services/league'
import SeasonPicker from '../components/SeasonPicker'
import StandingsTable from '../components/StandingsTable'
import { Card, Loading, Notice, Segmented } from '../components/ui'
import type { Conference } from '../types'

type View = 'AFC' | 'NFC' | 'overall'

export default function Standings() {
  const seasons = useAsync(fetchSeasons, [])
  const [selectedSeason, setSelectedSeason] = useState<number | null>(null)
  const [view, setView] = useState<View>('AFC')

  const currentSeason = seasons.data?.find(season => season.isCurrent)
  const seasonNumber = selectedSeason ?? currentSeason?.number

  const standings = useAsync(() => fetchStandings(seasonNumber), [seasonNumber])

  const rows =
    view === 'overall'
      ? (standings.data?.overall ?? [])
      : view === 'AFC'
        ? (standings.data?.afc ?? [])
        : (standings.data?.nfc ?? [])

  const unassigned = standings.data?.unassigned ?? []

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <span className="eyebrow">Standings</span>
          <h1>{seasonNumber ? `Season ${seasonNumber}` : 'Standings'}</h1>
          <p>
            Conferences are seeded by record, then the league tiebreakers — head-to-head
            for two; for three or more, a sweep or the best point differential is pulled
            out and the rest start over, so the last two still go head-to-head. Overall is
            a straight record ranking across both. Pick a row to see that player&rsquo;s
            results for the season.
          </p>
        </div>
        {seasons.data && seasons.data.length > 0 && (
          <SeasonPicker
            seasons={seasons.data}
            value={seasonNumber ?? seasons.data[0].number}
            onChange={next => setSelectedSeason(next === 'all' ? null : next)}
          />
        )}
      </header>

      {standings.error && <Notice tone="error">{standings.error}</Notice>}

      <Card
        title="Conference tables"
        actions={
          <Segmented<View>
            value={view}
            onChange={setView}
            options={[
              { value: 'AFC', label: 'AFC', className: 'is-afc' },
              { value: 'NFC', label: 'NFC', className: 'is-nfc' },
              { value: 'overall', label: 'Overall' },
            ]}
          />
        }
        conference={view === 'overall' ? null : (view as Conference)}
        flush
      >
        {standings.loading ? (
          <div style={{ padding: 24 }}>
            <Loading />
          </div>
        ) : (
          <StandingsTable
            rows={rows}
            showConference={view === 'overall'}
            showGamesLink
            showRespins={standings.data?.showRespins ?? false}
            seeded={view !== 'overall'}
            seasonNumber={seasonNumber}
          />
        )}
      </Card>

      {unassigned.length > 0 && (
        <Card
          title="Unassigned players"
          subtitle="Played this season but not placed in a conference"
          flush
        >
          <StandingsTable
            rows={unassigned}
            showGamesLink
            showRespins={standings.data?.showRespins ?? false}
            seasonNumber={seasonNumber}
          />
        </Card>
      )}
    </div>
  )
}
